import type { DocumentTypeValue } from './Shipping';

/**
 * Dirección de entrega del usuario.
 * GET /user/addresses (addresses_list), POST /user/address y PUT /user/address/{id}/document.
 */
export interface AddressDTO {
  id: number;
  idu: number;
  flag_idu: number | null; // 1 = dirección del propio usuario
  is_primary_address: number | null;
  // Quien recibe en esta dirección
  first_name: string | null;
  last_name: string | null;
  whatsapp: string | null;
  // Desde la app solo se registra DNI o CE, pero el admin guarda PASS y hay valores viejos
  // (ej. "Pasaporte"): tratarlo como texto libre.
  document_type: string | null;
  document_number: string | null;
  address_1: string;
  address_2: string | null;
  district: string;
  province: string;
  department: string;
  current_status: string;
}

/** Documento de quien recibe, tal como lo registra la app. */
export interface AddressDocumentPayload {
  document_type: DocumentTypeValue;
  document_number: string;
}

export const hasDocument = (address?: AddressDTO | null): boolean => !!address?.document_number;

/** "DNI 12345678"; '' si la dirección no tiene documento. */
export const addressDocumentLabel = (address?: AddressDTO | null): string =>
  hasDocument(address)
    ? [address?.document_type, address?.document_number].filter(Boolean).join(' ')
    : '';

/** Nombre de quien recibe guardado en la dirección; '' si viene vacío. */
export const addressReceiverName = (address?: AddressDTO | null): string =>
  [address?.first_name, address?.last_name].filter(Boolean).join(' ').trim();

/**
 * "Distrito, Provincia, Departamento" sin espacios de más (los datos pueden traer un espacio al
 * final de cada parte).
 */
export const addressLocationLine = (address?: AddressDTO | null): string =>
  [address?.district, address?.province, address?.department]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(', ');

/**
 * "Nombre · DNI 12345678" de quien recibe. Si la dirección no guarda el nombre,
 * usa fallbackName (el de la cuenta).
 */
export const addressReceiverLine = (address?: AddressDTO | null, fallbackName = ''): string =>
  [addressReceiverName(address) || fallbackName, addressDocumentLabel(address)]
    .filter(Boolean)
    .join(' · ');
